import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
    ArrowRight,
    Play,
    Share,
    Thermometer,
    FileCheck,
    ScanSearch,
    MapPin,
} from "lucide-react";
import { LogoMark } from "../../components/Logo";
import { fadeUp, viewportOnce } from "../../lib/motion";
import {
    HERO_IMAGES,
    HERO_TRUST,
    SIDEBAR_ITEMS,
    VIEW_CHIPS,
    CHANGE_LOG,
} from "../../lib/product";
import { DemoChip } from "./shared";

const TRUST_ICONS = {
    "file-check": FileCheck,
    "scan-search": ScanSearch,
    "map-pin": MapPin,
};

const VIEW_MODES = [
    { id: "plan", label: "PLAN" },
    { id: "3d", label: "3D" },
    { id: "realistic", label: "REALISTIC" },
];

const BudgetRing = () => {
    const r = 26;
    const c = 2 * Math.PI * r;
    return (
        <svg
            viewBox="0 0 64 64"
            className="h-16 w-16 shrink-0"
            aria-hidden="true"
        >
            <circle
                cx="32"
                cy="32"
                r={r}
                fill="none"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="5"
            />
            <circle
                cx="32"
                cy="32"
                r={r}
                fill="none"
                stroke="#34D399"
                strokeWidth="5"
                strokeDasharray={c}
                strokeDashoffset={c * (1 - 0.69)}
                transform="rotate(-90 32 32)"
            />
            <text
                x="32"
                y="36"
                textAnchor="middle"
                fill="#FFFFFF"
                fontSize="13"
                fontFamily="Space Grotesk, sans-serif"
            >
                69%
            </text>
        </svg>
    );
};

const HeroPanel = () => {
    const [view, setView] = useState("3d");
    const reduceMotion = useReducedMotion();
    const activeImg =
        view === "plan"
            ? HERO_IMAGES.plan
            : view === "realistic"
                ? HERO_IMAGES.realistic
                : HERO_IMAGES.house3d;

    return (
        <div
            data-testid="hero-ui-panel"
            className="border border-white/12 bg-[#0C1428] shadow-[0_32px_80px_rgba(0,0,0,0.5)]"
        >
            <div className="flex items-center justify-between gap-4 border-b border-white/10 px-5 py-4">
                <div className="flex items-center gap-3">
                    <LogoMark className="h-6 w-6" />
                    <div>
                        <p className="text-sm font-semibold text-white">
                            Home Vision
                        </p>
                        <p className="text-[13px] text-mist">
                            See your home before you build it.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="hidden items-center gap-1.5 font-mono text-[13px] tracking-[0.18em] text-mist md:inline-flex">
                        <MapPin className="h-3 w-3" />
                        DURBAN, KZN
                        <Thermometer className="ml-1 h-3 w-3" />
                        21°C
                    </span>
                    <button
                        type="button"
                        data-testid="hero-ui-share"
                        aria-label="Share design (demonstration control)"
                        className="inline-flex h-8 w-8 items-center justify-center border border-white/15 text-mist transition-colors duration-300 hover:border-accent hover:text-accent"
                    >
                        <Share className="h-3.5 w-3.5" />
                    </button>
                    <span className="inline-flex items-center bg-accent px-3 py-1.5 text-[14px] font-semibold text-white">
                        Save Design
                    </span>
                </div>
            </div>

            <div className="grid lg:grid-cols-[150px_1fr_250px]">
                <aside
                    aria-label="Product navigation (demonstration)"
                    className="hidden border-r border-white/10 px-3 py-4 lg:block"
                >
                    {SIDEBAR_ITEMS.map((item) => (
                        <div
                            key={item}
                            className={`flex items-center gap-2 px-3 py-2 text-[14px] ${
                                item === "Home Vision"
                                    ? "border-l-2 border-accent bg-accent/10 font-semibold text-accent"
                                    : "text-mist"
                            }`}
                        >
                            <span className="h-1 w-1 rounded-full bg-current opacity-60" />
                            {item}
                        </div>
                    ))}
                </aside>

                <div className="px-5 py-5">
                    <div className="flex items-center justify-between">
                        <div
                            role="group"
                            aria-label="View mode (demonstration)"
                            className="flex items-center gap-4"
                        >
                            {VIEW_MODES.map((mode) => (
                                <button
                                    key={mode.id}
                                    type="button"
                                    data-testid={`hero-view-toggle-${mode.id}`}
                                    aria-pressed={view === mode.id}
                                    onClick={() => setView(mode.id)}
                                    className={`pb-1.5 text-[13px] font-semibold tracking-[0.18em] transition-colors duration-300 ${
                                        view === mode.id
                                            ? "border-b border-accent text-accent"
                                            : "border-b border-transparent text-cad hover:text-white"
                                    }`}
                                >
                                    {mode.label}
                                </button>
                            ))}
                        </div>
                        <DemoChip>DEMO UI</DemoChip>
                    </div>

                    <div className="relative mt-4 aspect-[4/3] overflow-hidden border border-white/10">
                        <motion.img
                            key={activeImg}
                            src={activeImg}
                            alt="Contemporary double-storey South African home with large glazing and warm evening lighting"
                            initial={{ opacity: reduceMotion ? 1 : 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.7, ease: "easeOut" }}
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                        <span className="absolute right-3 top-3 bg-accent px-2 py-1 font-mono text-[13px] tracking-[0.22em] text-white">
                            EXTERIOR
                        </span>
                        <span className="absolute bottom-3 left-3 border border-white/30 bg-[#0A0F1D]/70 px-2 py-1 font-mono text-[13px] tracking-[0.22em] text-white/90 backdrop-blur-sm">
                            INTERIOR
                        </span>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                        {VIEW_CHIPS.map((chip, i) => (
                            <span
                                key={chip}
                                className={`px-3 py-1.5 text-[13px] ${
                                    i === 0
                                        ? "bg-accent font-semibold text-white"
                                        : "border border-white/15 text-mist"
                                }`}
                            >
                                {chip}
                            </span>
                        ))}
                    </div>
                </div>

                <aside
                    data-testid="hero-cost-panel"
                    className="border-t border-white/10 px-5 py-5 lg:border-l lg:border-t-0"
                >
                    <div className="flex items-center justify-between">
                        <p className="font-mono text-[13px] tracking-[0.22em] text-white">
                            DESIGN TO COST IMPACT
                        </p>
                        <DemoChip>DEMO</DemoChip>
                    </div>
                    <p className="mt-4 font-mono text-[13px] tracking-[0.22em] text-cad">
                        CURRENT ESTIMATE (INCL. VAT)
                    </p>
                    <p className="mt-1 font-display text-2xl font-semibold text-white">
                        R2,450,000
                    </p>
                    <div className="mt-4 flex items-center gap-4">
                        <BudgetRing />
                        <div className="font-mono text-[13px] leading-relaxed tracking-[0.18em] text-cad">
                            <p className="text-mist">69% OF DESIGN BUDGET</p>
                            <p className="mt-1 text-emerald-400">
                                +4.2% FROM PREVIOUS WEEK
                            </p>
                        </div>
                    </div>
                    <p className="mt-5 font-mono text-[13px] tracking-[0.22em] text-cad">
                        CHANGE LOG
                    </p>
                    <ul className="mt-2 space-y-2">
                        {CHANGE_LOG.map((row) => (
                            <li
                                key={row.label}
                                className="flex items-start justify-between gap-3 border-b border-white/[0.06] pb-2 text-[13px]"
                            >
                                <span className="text-mist">{row.label}</span>
                                <span
                                    className={`shrink-0 font-mono ${
                                        row.tone === "accent"
                                            ? "text-emerald-400"
                                            : "text-cad"
                                    }`}
                                >
                                    {row.value}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <span className="mt-4 flex items-center justify-center border border-white/20 px-4 py-2 text-[14px] font-medium text-white/80">
                        Compare Designs
                    </span>
                    <p className="mt-3 font-mono text-[13px] leading-relaxed tracking-[0.18em] text-cad">
                        ILLUSTRATIVE DATA — NOT A LIVE ESTIMATE
                    </p>
                </aside>
            </div>
        </div>
    );
};

const ProductHero = () => (
    <section
        id="product-hero"
        data-testid="product-hero-section"
        className="relative overflow-hidden bg-ink pt-[120px] pb-20 lg:pt-40 lg:pb-28"
    >
        <span
            aria-hidden="true"
            className="absolute inset-x-0 top-0 h-px bg-white/[0.06]"
        />
        <div className="relative mx-auto grid max-w-[1600px] items-center gap-14 px-6 md:px-10 lg:grid-cols-12 lg:gap-10 lg:px-16">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                className="lg:col-span-5"
            >
                <p
                    data-testid="product-eyebrow"
                    className="font-mono text-[13px] tracking-[0.3em] text-accent"
                >
                    THE BUILDEASE PLATFORM
                </p>
                <h1
                    data-testid="product-hero-headline"
                    className="mt-6 font-display text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-[3.4rem]"
                >
                    From architectural plan to{" "}
                    <span className="text-accent">construction clarity.</span>
                </h1>
                <p className="mt-6 max-w-md text-[16px] leading-relaxed text-mist">
                    BuildEase turns your architectural plans into a visual,
                    cost-aware project you can understand, explore and manage —
                    before construction begins.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                    <Link
                        to="/home-vision"
                        data-testid="hero-cta-home-vision"
                        className="group inline-flex items-center gap-2 bg-accent px-6 py-3.5 text-[16px] font-semibold text-white transition-colors duration-300 hover:bg-[#ff7026]"
                    >
                        Explore Home Vision
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </Link>
                    <Link
                        to="/how-it-works"
                        data-testid="hero-cta-how-it-works"
                        className="inline-flex items-center gap-2.5 border border-white/25 px-6 py-3.5 text-[16px] font-medium text-white transition-colors duration-300 hover:border-accent"
                    >
                        <Play className="h-3.5 w-3.5" />
                        See How It Works
                    </Link>
                </div>
                <ul className="mt-10 grid gap-5 border-t border-white/10 pt-6 sm:grid-cols-3">
                    {HERO_TRUST.map((item) => {
                        const Icon = TRUST_ICONS[item.icon];
                        return (
                            <li
                                key={item.title}
                                data-testid={`hero-trust-${item.title
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`}
                                className="flex items-start gap-2.5"
                            >
                                <Icon
                                    className="mt-0.5 h-4 w-4 shrink-0 text-mist"
                                    strokeWidth={1.5}
                                />
                                <div>
                                    <p className="text-[14px] font-semibold tracking-wide text-white">
                                        {item.title}
                                    </p>
                                    <p className="mt-1 text-[13px] leading-snug text-cad">
                                        {item.copy}
                                    </p>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 36 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="lg:col-span-7"
            >
                <HeroPanel />
            </motion.div>
        </div>
    </section>
);

export default ProductHero;
